import { AuthContext, type AuthContextValue } from "./authContextState";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authService } from "@/api/authService";
import { gymsService } from "@/api/gymsService";
import {
  clearPortalAuthSnapshot,
  getPortalAuthSnapshot,
  setPortalAuthSnapshot,
} from "@/lib/authStorage";
import {
  clearTokens,
  getAccessToken,
  setAccessToken,
  setIsAdminView,
} from "@/lib/tokenService";
import {
  identifyPortalSession,
  resetPortalAnalytics,
} from "@/lib/analytics";
import { getApiFailureKind } from "@/lib/apiError";
import type {
  AdminViewExchangeResponseDO,
  AuthFitnessCenterDO,
  AuthStaffDO,
  LoginResponseDO,
} from "@/types/auth";

const buildAdminViewUser = (
  response: AdminViewExchangeResponseDO,
): AuthStaffDO => ({
  _id: response.adminViewerId,
  firstName: "Admin",
  lastName: "(View Only)",
  email: "",
  role: "Admin",
  isOwner: false,
});

const buildAdminViewGym = (
  response: AdminViewExchangeResponseDO,
): AuthFitnessCenterDO => ({
  _id: response.fitnessCenterId,
  name: response.fitnessCenterName ?? "Admin View",
  countryCode: response.countryCode ?? "",
  timezone: response.timezone ?? "UTC",
  currencyCode: response.currencyCode ?? "",
  memberAccountMode: response.memberAccountMode ?? undefined,
  parentCenterId:
    response.rootCenterId !== response.fitnessCenterId
      ? response.rootCenterId
      : null,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [initialSnapshot] = useState(() => getPortalAuthSnapshot());
  const [pendingAdminViewToken] = useState<string | null>(() => {
    const token = sessionStorage.getItem("pendingAdminViewToken");
    if (token) sessionStorage.removeItem("pendingAdminViewToken");
    return token;
  });
  const [user, setUser] = useState<AuthStaffDO | null>(
    initialSnapshot?.user ?? null
  );
  const [activeGym, setActiveGym] = useState<AuthFitnessCenterDO | null>(
    initialSnapshot?.activeGym ?? null
  );
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAdminView, setIsAdminViewState] = useState(false);

  const clearAuthState = useCallback(() => {
    clearPortalAuthSnapshot();
    clearTokens(); // also clears isAdminView flag in tokenService
    setUser(null);
    setActiveGym(null);
    setIsAdminViewState(false);
    resetPortalAnalytics();
  }, []);

  const setAuthSession = useCallback((response: LoginResponseDO) => {
    setAccessToken(response.accessToken);
    setPortalAuthSnapshot({
      user: response.staff,
      activeGym: response.fitnessCenter,
    });
    setUser(response.staff);
    setActiveGym(response.fitnessCenter);
    identifyPortalSession(response.staff, response.fitnessCenter);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      if (pendingAdminViewToken) {
        try {
          const response =
            await authService.exchangeAdminViewToken(pendingAdminViewToken);
          if (!isMounted) return;
          setAccessToken(response.accessToken);
          setIsAdminView(true);
          setIsAdminViewState(true);
          setUser(buildAdminViewUser(response));
          setActiveGym(buildAdminViewGym(response));
        } catch (error) {
          if (!isMounted) return;
          toast.error(
            error instanceof Error
              ? error.message
              : "Admin view session is invalid or expired",
          );
        } finally {
          if (isMounted) setIsAuthReady(true);
        }
        return;
      }

      if (!initialSnapshot) {
        setIsAuthReady(true);
        return;
      }

      if (getAccessToken()) {
        setIsAuthReady(true);
        return;
      }

      try {
        const response = await authService.refreshToken();
        if (!isMounted) {
          return;
        }
        setAccessToken(response.accessToken);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        const failureKind = getApiFailureKind(error);
        if (failureKind === "transport" || failureKind === "timeout" || failureKind === "server") {
          toast.error("Connection interrupted. We will retry when you reconnect.");
        } else {
          clearAuthState();
        }
      } finally {
        if (isMounted) {
          setIsAuthReady(true);
        }
      }
    };

    void bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, [clearAuthState, initialSnapshot, pendingAdminViewToken]);

  useEffect(() => {
    if (user && activeGym) {
      identifyPortalSession(user, activeGym);
    }
  }, [activeGym, user]);

  const canSwitchBranch = user?.role === "Admin";
  const isAuthenticated = Boolean(user);

  const gymsQuery = useQuery({
    queryKey: ["portalGyms", isAdminView ? "adminView" : "me"],
    queryFn: isAdminView
      ? authService.fetchAdminViewGyms
      : gymsService.fetchMyGyms,
    enabled: canSwitchBranch && isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  const switchBranchMutation = useMutation({
    mutationFn: (payload: { branchId: string }) =>
      isAdminView
        ? authService.switchAdminViewBranch(payload)
        : authService.switchBranch(payload),
    onSuccess: async (response) => {
      if (isAdminView) {
        setAccessToken(response.accessToken);
        setIsAdminView(true);
        setIsAdminViewState(true);
        setActiveGym(buildAdminViewGym(response));
      } else {
        setAuthSession(response);
      }
      await queryClient.cancelQueries();
      await queryClient.invalidateQueries();
      await queryClient.refetchQueries({ type: "active" });
      toast.success(response.message);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Branch switch failed");
    },
  });

  const gyms = useMemo(() => {
    if (canSwitchBranch && gymsQuery.data) {
      return [gymsQuery.data.root, ...gymsQuery.data.branches];
    }

    return activeGym ? [activeGym] : [];
  }, [activeGym, canSwitchBranch, gymsQuery.data]);

  const selectedBranch = useMemo(() => {
    if (!activeGym) {
      return null;
    }

    return gyms.find((gym) => gym._id === activeGym._id) ?? activeGym;
  }, [activeGym, gyms]);

  const setSelectedBranchId = useCallback(
    (branchId: string) => {
      if (!canSwitchBranch || !activeGym || branchId === activeGym._id) {
        return;
      }

      void switchBranchMutation.mutateAsync({ branchId });
    },
    [activeGym, canSwitchBranch, switchBranchMutation]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      activeGym,
      gyms,
      rootGymId: gymsQuery.data?.root._id ?? null,
      canSwitchBranch,
      selectedBranchId: activeGym?._id ?? "",
      selectedBranch,
      isAuthenticated,
      isAuthReady,
      isSwitchingBranch: switchBranchMutation.isPending,
      isAdminView,
      setSelectedBranchId,
      setAuthSession,
      clearAuthState,
    }),
    [
      activeGym,
      canSwitchBranch,
      clearAuthState,
      gyms,
      gymsQuery.data,
      isAdminView,
      isAuthenticated,
      isAuthReady,
      selectedBranch,
      setAuthSession,
      setSelectedBranchId,
      switchBranchMutation.isPending,
      user,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

